import {
  type FormikConfig,
  type FormikHelpers,
  type FormikValues,
  useFormik,
} from 'formik';
import useBooking, { type Booking } from 'hooks/useBooking';

import usePrevious from 'hooks/usePrevious';
import useStoredBooking, {
  getInitialValues,
  type StoredBooking,
} from 'hooks/useStoredBooking';
import useUpdateEffect from 'hooks/useUpdateEffect';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { BasketContext } from 'state/basket';

import {
  combineBookingItems,
  combineSlots,
  generateBookingItemFields,
  getBookingState,
} from 'utils/booking';

import {
  type FormatBasketOptions,
  formatBasketPayload,
} from '../../services/formatBasketPayload';
import { formatUpdateBookingPayload } from '../../services/formatUpdateBookingPayload';

import { getValidator } from '../../validators/booking';

interface Service {
  id: number;
}

interface Options {
  services?: Service[];
}

// The extra context this hook's submit handler receives beyond Formik's own
// two arguments.
export interface BookingSubmitExtras {
  existingBooking?: Booking;
}

type BookingFormikConfig = Omit<
  Partial<FormikConfig<FormikValues>>,
  'onSubmit'
> & {
  onSubmit?: (
    values: FormikValues,
    helpers: FormikHelpers<FormikValues>,
    extraData: BookingSubmitExtras
  ) => unknown;
};

const useFormikBooking = (
  enabledOption: boolean | ((values: unknown) => boolean) = true,
  {
    initialValues,
    enableReinitialize: enableReinitializeOption = false,
    onSubmit: onSubmitOption,
    ...options
  }: BookingFormikConfig = {},
  enableSubmission = false,
  { services }: Options = {}
) => {
  const [changesSaved, setChangesSaved] = useState<boolean>(false);

  const [
    storedValues,
    setStorage,
    removeStorage,
    storageInitialising,
    receivedUpdate,
  ] = useStoredBooking(initialValues as Parameters<typeof useStoredBooking>[0]);

  const [formValues, setFormValues] = useState(storedValues);

  useEffect(() => {
    setFormValues(storedValues);
  }, [storedValues]);

  const enabled = useMemo(
    () =>
      enabledOption instanceof Function
        ? enabledOption(storedValues)
        : !!enabledOption,
    [enabledOption, storedValues]
  );

  const { actions: { submitBookingToBasket, updateBooking } = {} } =
    useContext(BasketContext);

  const { data: existingBooking } = useBooking(
    storedValues.id ? Number(storedValues.id) : undefined,
    enableSubmission
  );

  const bookingState = useMemo(
    () => getBookingState(existingBooking),
    [existingBooking]
  );

  const onSubmit = async (
    values: FormikValues,
    helpers: FormikHelpers<FormikValues>
  ) => {
    const extraData = { existingBooking };

    const {
      id,
      venueId,
      spaceIds,
      date,
      slots,
      service,
      host: { value: host } = {},
      recurrence = {},
      bookingItems = {},
    } = values;

    const { setSubmitting } = helpers;

    const submitBooking = (payload: FormatBasketOptions) =>
      id
        ? updateBooking!({ id }, formatUpdateBookingPayload(payload))
        : submitBookingToBasket!(formatBasketPayload(payload));

    await submitBooking({
      date: new Date(date),
      venueId,
      spaceIds,
      slots: combineSlots(slots),
      clientServiceId: service,
      bookingHostId: host,
      ...(recurrence.enabled ? { recurrence } : {}),
      bookingItems: combineBookingItems(bookingItems),
      initialBookingItems: getInitialValues({
        ...(existingBooking || {}),
        clientService: undefined,
        recurrenceRules: undefined,
      } as unknown as Parameters<typeof getInitialValues>[0]).bookingItems,
    });

    try {
      return await Promise.resolve(
        onSubmitOption?.(values, helpers, extraData)
      );
    } finally {
      setSubmitting(false);
      setChangesSaved(true);
      removeStorage();
    }
  };

  // Reinitialise Formik exactly once after storage has initialised.
  const initialising = usePrevious<boolean>(storageInitialising, true);
  const updatesEnabled = !initialising;
  const enableReinitialize = !!(
    enableReinitializeOption ||
    initialising ||
    receivedUpdate
  );

  const validator = useMemo(
    () => getValidator({ services, state: bookingState }),
    [services, bookingState]
  );

  const formik = useFormik({
    ...options,
    initialValues: initialising ? storedValues : formValues,
    enableReinitialize,
    validate: validator,
    onSubmit: (enableSubmission
      ? onSubmit
      : () => null) as FormikConfig<FormikValues>['onSubmit'],
  });

  const { values, setFieldValue, resetForm } = formik;

  const sendUpdatesRef = useRef<boolean>(false);

  useEffect(() => {
    sendUpdatesRef.current = !initialising && !receivedUpdate;
  }, [initialising, receivedUpdate]);

  useEffect(() => {
    if (sendUpdatesRef.current) {
      setChangesSaved(false);

      if (values?.venueId !== undefined && enabled) {
        setStorage(values as StoredBooking);
      } else if (!enabled) {
        removeStorage();
      }
    }

    sendUpdatesRef.current = true;
  }, [enabled, values, setStorage, removeStorage]);

  const generateBookingItems = useCallback(() => {
    const numericFields = [
      'everyNumberOf',
      'nthDayOfMonth',
      'weekNumber',
      'count',
    ];

    const numberOrUndefined = (value: unknown) => {
      const number = Number(value);
      return Number.isNaN(number) || value === '' ? undefined : number;
    };

    return generateBookingItemFields(
      values.date,
      values.spaceIds,
      values.slots,
      values.recurrence.enabled
        ? Object.keys(values.recurrence)
            .map(key => ({
              [key]: numericFields.includes(key)
                ? numberOrUndefined(values.recurrence[key])
                : values.recurrence[key],
            }))
            .reduce((prev, curr) => ({ ...prev, ...curr }), {})
        : undefined
    );
  }, [values.date, values.spaceIds, values.slots, values.recurrence]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps array is intentionally controlled by caller
  useUpdateEffect(
    () => {
      if (!values.recurrence.enabled) {
        setFieldValue('bookingItems', generateBookingItems());
      }
    },
    [values.recurrence.enabled],
    updatesEnabled
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps array is intentionally controlled by caller
  useUpdateEffect(
    () => {
      setFieldValue('recurrence.checked', false);
    },
    [],
    updatesEnabled
  );

  const clearAll = useCallback(
    async (...args: Parameters<typeof getInitialValues>) => {
      await resetForm({ values: getInitialValues(...args) });
      removeStorage();
    },
    [resetForm, removeStorage]
  );

  const combinedSlots = useMemo(
    () => combineSlots(values.slots),
    [values.slots]
  );

  return {
    formik,
    initialising,
    existingBooking,
    changesSaved,
    setChangesSaved,
    generateBookingItems,
    clearAll,
    removeStorage,
    combinedSlots,
  };
};

export default useFormikBooking;
